import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Buildings, Plus, ChartPieSlice, TrendUp, TrendDown, CurrencyInr, PaperPlaneTilt, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import Layout from "../../components/Layout";
import SendGmailModal from "../../components/SendGmailModal";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";

export default function FinanceDashboard() {
  const { active, activeId, companies } = useCompany();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [paySummary, setPaySummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [nudgeTarget, setNudgeTarget] = useState(null);

  const loadOverdue = () => {
    if (!activeId) return;
    api.get("/finance/payments/receivables/overdue", { params: { company_id: activeId, min_days: 15 } })
       .then(r => setOverdue(r.data)).catch(() => setOverdue(null));
  };

  useEffect(() => {
    if (activeId) {
      api.get("/finance/dashboard", { params: { company_id: activeId } }).then(r => setData(r.data));
      api.get("/finance/payments/summary/dashboard", { params: { company_id: activeId } }).then(r => setPaySummary(r.data)).catch(() => {});
      api.get("/finance/payments/health/business", { params: { company_id: activeId } }).then(r => setHealth(r.data)).catch(() => {});
      const month = new Date().toISOString().slice(0,7);
      api.get("/finance/payments/cashflow/by-source", { params: { month } }).then(r => setCashflow(r.data)).catch(() => {});
      loadOverdue();
    } else { setData(null); setPaySummary(null); setHealth(null); setCashflow(null); setOverdue(null); }
  }, [activeId]);

  if (companies.length === 0) {
    return (
      <Layout title="Reyansh · Finance" subtitle="Profit-tracking across all your work orders">
        <div className="border border-black/10 p-16 text-center bg-white" data-testid="no-companies-empty">
          <div className="inline-flex items-center gap-2 mb-3"><span className="w-2 h-2 bg-[#D90429]"/><span className="font-mono-shelv text-[10px] tracking-[0.2em] uppercase text-[#D90429]">Welcome to Reyansh</span></div>
          <h2 className="font-display text-4xl font-black tracking-tighter">Track every rupee. Across every PO.</h2>
          <p className="mt-4 text-[#4A4D57] max-w-xl mx-auto">Start by adding a company. You can manage multiple companies (Shelvestech Pvt Ltd, Shelvestech Trading, etc.) under one login.</p>
          <button onClick={() => navigate("/finance/companies")} data-testid="cta-add-first-company" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#D90429] text-white font-semibold hover:bg-[#EF233C] active:scale-95">
            <Plus size={16} weight="bold"/> Add your first company
          </button>
        </div>
      </Layout>
    );
  }

  if (!activeId || !data) {
    return <Layout title="Reyansh · Finance"><div className="p-10 text-center text-[#717680]">Pick a company from the sidebar.</div></Layout>;
  }

  const statusData = Object.entries(data.by_status || {}).map(([name, value]) => ({ name, value }));
  const splitData = [
    { name: "Net profit", value: Math.max(data.net_profit_inr, 0) },
    { name: "Expenses", value: data.total_expenses_inr },
    { name: "Overhead", value: data.overhead_inr },
  ];
  const COLORS = ["#2A9D8F", "#D90429", "#F4A261"];

  return (
    <Layout title="Reyansh · Finance" subtitle={`${active?.name} · ${data.total_work_orders} work orders`}>
      {/* Big P&L row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border border-black/10 bg-white mb-6" data-testid="finance-dashboard-stats">
        {[
          {label:"Total Value", value: data.total_value_inr, icon: CurrencyInr, color:"#0A0A0A", testId:"stat-total-value"},
          {label:"Received", value: data.total_payments_inr, icon: TrendUp, color:"#2A9D8F", testId:"stat-received"},
          {label:"Outstanding", value: data.outstanding_inr, icon: ChartPieSlice, color:"#F4A261", testId:"stat-outstanding"},
          {label:"Expenses", value: data.total_expenses_inr, icon: TrendDown, color:"#D90429", testId:"stat-expenses"},
          {label:"Net Profit", value: data.net_profit_inr, icon: TrendUp, color: data.net_profit_inr>=0?"#2A9D8F":"#D90429", testId:"stat-net-profit"},
        ].map((s)=>(
          <div key={s.label} data-testid={s.testId} className="p-5 border-r border-black/5 last:border-r-0">
            <div className="flex items-center justify-between">
              <div className="overline" style={{color:s.color}}>{s.label}</div>
              <s.icon size={18} weight="bold" style={{color:s.color}}/>
            </div>
            <div className="font-display text-3xl font-black tracking-tighter mt-2" style={{color:s.color}}>{formatINR(s.value)}</div>
          </div>
        ))}
      </div>

      {/* Phase 2A: Net Outstanding (receivable) + Net Payable (vouchers + opex) tiles */}
      {paySummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-6" data-testid="pay-summary-tiles">
          <div className="border border-[#0D9488]/30 bg-[#0D9488]/5 p-4">
            <div className="overline text-[10px] text-[#0D9488]">Net Receivable</div>
            <div className="font-display text-2xl font-black tracking-tighter text-[#0D9488] mt-1" data-testid="tile-net-receivable">{formatINR(paySummary.net_receivable)}</div>
            {paySummary.net_receivable_overdue_45_days > 0 && (
              <div className="text-[10px] text-[#D90429] font-mono-shelv uppercase tracking-widest mt-1">
                ⚠ {formatINR(paySummary.net_receivable_overdue_45_days)} overdue 45+ days
              </div>
            )}
          </div>
          <div className="border border-[#D90429]/30 bg-[#D90429]/5 p-4">
            <div className="overline text-[10px] text-[#D90429]">Net Payable · Vouchers</div>
            <div className="font-display text-2xl font-black tracking-tighter text-[#D90429] mt-1" data-testid="tile-net-payable-vouchers">{formatINR(paySummary.net_payable_vouchers)}</div>
            {paySummary.net_payable_overdue_30_days > 0 && (
              <div className="text-[10px] text-[#D90429] font-mono-shelv uppercase tracking-widest mt-1">
                ⚠ {formatINR(paySummary.net_payable_overdue_30_days)} pending 30+ days
              </div>
            )}
          </div>
          <div className="border border-[#F4A261]/30 bg-[#F4A261]/5 p-4">
            <div className="overline text-[10px] text-[#F4A261]">Operating Expenses Outstanding</div>
            <div className="font-display text-2xl font-black tracking-tighter text-[#F4A261] mt-1" data-testid="tile-opex-outstanding">{formatINR(paySummary.net_payable_opex)}</div>
          </div>
          <div className="border border-black/15 bg-[#0A0A0A] text-white p-4">
            <div className="overline text-[10px] text-white/70">Total Payables</div>
            <div className="font-display text-2xl font-black tracking-tighter mt-1" data-testid="tile-total-payables">{formatINR(paySummary.net_payable_total)}</div>
            <div className="text-[10px] text-white/50 font-mono-shelv uppercase tracking-widest mt-1">Vouchers + Opex</div>
          </div>
        </div>
      )}

      {/* Phase 2B: Business Health panel */}
      {health && (
        <div className="mb-6 border border-black/10 bg-white" data-testid="business-health-panel">
          <div className="px-4 py-3 border-b border-black/10 bg-[#0A0A0A] text-white flex items-center justify-between">
            <div>
              <div className="overline text-[#D90429]">Business Health</div>
              <div className="font-display text-xl font-bold tracking-tight">Live operational pulse</div>
            </div>
            <div className="text-right">
              <div className="overline text-[10px] text-white/60">Net Profit (indicative)</div>
              <div className={`font-display text-2xl font-black tracking-tighter ${health.net_profit_indicative >= 0 ? "text-[#0D9488]" : "text-[#D90429]"}`} data-testid="health-net-profit">
                {formatINR(health.net_profit_indicative)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
            {[
              { l: "Sales Pipeline", v: health.sales_pipeline, c: "#3A86FF", t: "hp-pipeline" },
              { l: "Sales Done",     v: health.sales_done,     c: "#0D9488", t: "hp-sales-done" },
              { l: "Receivables",    v: health.receivables,    c: "#F4A261", t: "hp-receivables" },
              { l: "Inventory On-Hand", v: health.inventory_on_hand_value || 0, c: "#0D9488", t: "hp-inventory-on-hand" },
              { l: "COGS Realised", v: health.cogs_realised || 0, c: "#7C3AED", t: "hp-cogs-realised" },
              { l: "Expenses",       v: health.expenses,       c: "#D90429", t: "hp-expenses" },
              { l: "Payables",       v: health.payables,       c: "#D90429", t: "hp-payables" },
            ].map((s) => (
              <div key={s.l} data-testid={s.t} className="p-4 border-r border-b border-black/5 last:border-r-0">
                <div className="overline text-[10px]" style={{ color: s.c }}>{s.l}</div>
                <div className="font-display text-xl md:text-2xl font-black tracking-tighter mt-1" style={{ color: s.c }}>{formatINR(s.v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-source cash-flow */}
      {cashflow && (
        <div className="mb-6 border border-black/10 bg-white" data-testid="cashflow-by-source">
          <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between bg-[#F5F6F8]">
            <div>
              <div className="overline">Cash-flow · Source</div>
              <div className="font-display text-lg font-bold tracking-tight">Bank-wise outflow · {cashflow.month}</div>
            </div>
            <div className="font-mono-shelv text-sm">Total: <b>{formatINR(cashflow.grand_total)}</b></div>
          </div>
          {cashflow.rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#717680] text-sm">No payments recorded in {cashflow.month} yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv bg-[#FAFBFC]">
                <tr><th className="px-4 py-2">Source</th><th className="px-4 py-2 text-right"># Payments</th><th className="px-4 py-2 text-right">Invoices</th><th className="px-4 py-2 text-right">Vouchers</th><th className="px-4 py-2 text-right">Expenses</th><th className="px-4 py-2 text-right">Total</th></tr>
              </thead>
              <tbody>
                {cashflow.rows.map((r) => (
                  <tr key={`src-${r.source}`} className="border-t border-black/5">
                    <td className="px-4 py-2 font-bold">{r.source}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv">{r.count}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv text-[#717680]">{formatINR(r.by_type.invoice || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv text-[#717680]">{formatINR(r.by_type.voucher || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv text-[#717680]">{formatINR(r.by_type.expense || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono-shelv font-bold">{formatINR(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Who owes us — overdue receivables with one-tap Gmail nudge */}
      {overdue && overdue.count > 0 && (
        <div className="mb-6 border border-black/10 bg-white" data-testid="overdue-receivables-widget">
          <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between bg-[#0A0A0A] text-white">
            <div className="flex items-center gap-3">
              <Warning size={18} weight="fill" style={{ color: "#F4A261" }} />
              <div>
                <div className="overline text-[#F4A261]">Who owes us</div>
                <div className="font-display text-lg font-bold tracking-tight">{overdue.count} invoice{overdue.count > 1 ? "s" : ""} overdue · 15+ days</div>
              </div>
            </div>
            <div className="text-right">
              <div className="overline text-[10px] text-white/60">Total outstanding</div>
              <div className="font-display text-2xl font-black tracking-tighter text-[#F4A261]" data-testid="overdue-total">{formatINR(overdue.total_outstanding)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="text-left text-[10px] uppercase tracking-widest text-[#717680] font-mono-shelv bg-[#FAFBFC]">
                <tr>
                  <th className="px-4 py-2">Invoice #</th>
                  <th className="px-4 py-2">Client</th>
                  <th className="px-4 py-2 text-right">Outstanding</th>
                  <th className="px-4 py-2 text-right">Days overdue</th>
                  <th className="px-4 py-2">Tone</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {overdue.rows.slice(0, 8).map((r) => {
                  const toneColor = r.tone === "firm" ? "#D90429" : (r.tone === "reminder" ? "#F4A261" : "#0D9488");
                  return (
                    <tr key={r.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`overdue-row-${r.id}`}>
                      <td className="px-4 py-2 font-mono-shelv text-xs">{r.number || r.id.slice(-8)}</td>
                      <td className="px-4 py-2 font-semibold">{r.client_name}<div className="text-[10px] text-[#717680]">{r.client_email || "—"}</div></td>
                      <td className="px-4 py-2 text-right font-mono-shelv font-bold">{formatINR(r.outstanding)}</td>
                      <td className="px-4 py-2 text-right font-mono-shelv" style={{ color: toneColor }}>{r.days_overdue}d</td>
                      <td className="px-4 py-2">
                        <span className="text-[10px] font-mono-shelv uppercase tracking-widest px-2 py-0.5 border" style={{ color: toneColor, borderColor: toneColor + "60" }}>{r.tone}</span>
                        {r.email_log_count > 0 && <span className="ml-1 text-[10px] text-[#717680]">· sent {r.email_log_count}×</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => setNudgeTarget(r)}
                          disabled={!r.client_email}
                          title={r.client_email ? "Send polite nudge via Gmail" : "Add client email on the invoice first"}
                          data-testid={`nudge-btn-${r.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: toneColor }}
                        >
                          <PaperPlaneTilt size={12} weight="bold" /> Send Nudge
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {overdue.rows.length > 8 && (
              <div className="px-4 py-2 text-center text-xs text-[#717680] border-t border-black/5">
                + {overdue.rows.length - 8} more overdue invoice{overdue.rows.length - 8 > 1 ? "s" : ""} — <button onClick={() => navigate("/finance/billing")} className="text-[#D90429] font-semibold hover:underline">view in Billing →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Gmail nudge modal */}
      <SendGmailModal
        open={!!nudgeTarget}
        onOpenChange={(v) => { if (!v) setNudgeTarget(null); }}
        endpoint={nudgeTarget ? `/oauth/gmail/send/billing/${nudgeTarget.id}` : ""}
        defaultTo={nudgeTarget?.client_email || ""}
        defaultSubject={nudgeTarget?.nudge_subject || ""}
        defaultBody={nudgeTarget?.nudge_body_text || ""}
        attachmentName={nudgeTarget ? `Invoice_${nudgeTarget.number || nudgeTarget.id.slice(-6)}.pdf` : ""}
        kindLabel="Tax Invoice · Payment nudge"
        onSent={() => { setNudgeTarget(null); loadOverdue(); }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
        <div className="lg:col-span-7 border border-black/10 bg-white p-6">
          <div className="overline">Pipeline</div>
          <h3 className="font-display text-2xl font-bold tracking-tight mb-4">Work orders by status</h3>
          <div style={{ width:"100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={statusData}>
                <CartesianGrid stroke="#EBECEF" vertical={false}/>
                <XAxis dataKey="name" stroke="#717680" fontSize={12}/>
                <YAxis stroke="#717680" fontSize={12} allowDecimals={false}/>
                <Tooltip cursor={{fill:"#F5F6F8"}} contentStyle={{border:"1px solid #0A0A0A", borderRadius:0}}/>
                <Bar dataKey="value" fill="#D90429"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-5 border border-black/10 bg-white p-6">
          <div className="overline">Value breakdown</div>
          <h3 className="font-display text-2xl font-bold tracking-tight mb-4">Where the money goes</h3>
          <div style={{ width:"100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={splitData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {splitData.map((entry,i)=><Cell key={entry.name} fill={COLORS[i]}/>)}
                </Pie>
                <Tooltip contentStyle={{border:"1px solid #0A0A0A", borderRadius:0}} formatter={(v)=>formatINR(v)}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
            {splitData.map((d,i)=>(<div key={d.name} className="flex items-center gap-1.5"><span className="w-2 h-2" style={{background:COLORS[i]}}/><span>{d.name}</span></div>))}
          </div>
        </div>
      </div>

      <div className="border border-black/10 bg-white">
        <div className="flex items-center justify-between p-6 border-b border-black/10">
          <div><div className="overline">Activity</div><h3 className="font-display text-2xl font-bold tracking-tight mt-1">Recent work orders</h3></div>
          <button onClick={()=>navigate("/finance/work-orders")} data-testid="view-all-wos" className="text-sm font-semibold text-[#D90429] hover:underline">View all →</button>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F5F6F8] text-left text-xs">
            <tr>{["PO #","Title","Value","Received","Net P/L","Status"].map(h=>(<th key={h} className="px-6 py-3 font-mono-shelv uppercase tracking-widest text-[#717680]">{h}</th>))}</tr>
          </thead>
          <tbody>
            {(data.work_orders || []).slice(0,8).length===0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-[#717680]">No work orders yet. <button onClick={()=>navigate("/finance/work-orders")} className="text-[#D90429] font-semibold ml-1">Create one →</button></td></tr>}
            {(data.work_orders || []).slice(0,8).map(wo=>{
              const s = wo.summary;
              return (
                <tr key={wo.id} className="border-t border-black/5 hover:bg-[#FAFBFC] cursor-pointer" onClick={()=>navigate(`/finance/work-orders/${wo.id}`)}>
                  <td className="px-6 py-3 font-mono-shelv text-xs">{wo.po_number}</td>
                  <td className="px-6 py-3 font-semibold">{wo.title}</td>
                  <td className="px-6 py-3 font-mono-shelv">{formatINR(s.value_inr)}</td>
                  <td className="px-6 py-3 font-mono-shelv text-[#2A9D8F]">{formatINR(s.total_payments_inr)}</td>
                  <td className={`px-6 py-3 font-mono-shelv font-bold ${s.net_profit_inr>=0?"text-[#2A9D8F]":"text-[#D90429]"}`}>{formatINR(s.net_profit_inr)}</td>
                  <td className="px-6 py-3"><span className="text-xs font-mono-shelv uppercase">{wo.status}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </Layout>
  );
}
